<template>
  <div
    v-if="display"
  >
    <v-badge
      :value="shouldBadgeBeShown"
      :class="{
        'teacher-badge': (
          userType === 'teacher'
          && !isPointsBadge
        ),
        'student-badge': (
          userType !== 'teacher'
          && !isPointsBadge
        ),
        'points-badge': isPointsBadge,
      }"
      top
      overlap
      dot
      :color="(isPointsBadge && selectedBadgeColor) ? selectedBadgeColor.border : 'red'"
    >
      <span
        slot="badge"
        :style="{
          color: selectedBadgeColor.text
        }"
        v-dompurify-html="!isPointsBadge ? '' : badgeText"
      />
      <div
        class="full-width"
        v-dompurify-html="display"
      />
    </v-badge>
  </div>
</template>

<script>

import {
  INTERACTION_TYPES,
} from '@Constants';

import {
  mapGetters,
  mapState,
} from 'vuex';

export default {
  name: 'InstructorGradeCell',
  props: [
    'question',
    'data',
    'view',
    'userType',
    'showPointsBadges',
  ],
  data() {
    return {
      display: null,
      referenceQuestion: null,
      badgeText: '',
      selectedBadgeColor: null,
      badgeColors: {
        success: {
          background: '#e6f4ea',
          text: '#FFF',
          border: '#34a853',
        },
        warning: {
          background: '#fef7e0',
          text: '#4D4D4D',
          border: '#FBBC04',
        },
        danger: {
          background: '#FCE8E6',
          text: '#FFF',
          border: '#EA4335',
        },
        info: {
          background: '#F4F4F5',
          text: '#FFF',
          border: '#808080',
        },
      },
    };
  },
  computed: {
    ...mapState('instructors', [
      'savedGrade',
      'saveCount',
      'activeViewInstructor'
    ]),
    ...mapState('learners', [
      'activeViewLearners'
    ]),
    ...mapState([
      'viewedComments'
    ]),
    ...mapGetters([
      'learnerData',
      'instructorData',
    ]),
    delimiter() {
      return this?.question?.config?.hasNewFillInTheBlankDelimiter
        ? '^||*|||*||^'
        : ',';
    },
    hideBadge() {
      const userId = this.userType === 'teacher' ? this.instructorData.id : this.learnerData.id;
      const viewed = this.userType === 'teacher' ? _.get(this.data, 'comment_viewed.instructors_viewed') : _.get(this.data, 'comment_viewed.students_viewed');
      return !this.data.comment 
      || viewed.includes(userId)
      || this.viewedComments.includes(this.data.interaction_attempt_id);
    },
    hideCorrectAnswerAlways() {
      return (
        !!_.get(this, 'question.config.hide_correct_answer_always', false)
        && this.userType === 'student'
      );
    },
    isPointsBadge() {
      return (
        this.hideBadge
        && !!this.badgeText
      );
    },
    shouldBadgeBeShown() {
      return (
        !this.hideBadge
        || (
          this.isPointsBadge
          && !!this.showPointsBadges
        )
      );
    },
  },
  methods: {
    loadData() {
      //if student or single interaction, sets question info from data instead of being passed as parameter
      let question;
      if (
        this.question === 'student'
        || this.question === 'single'
      ) {
        question = {
          interaction_type: this.data.interaction_type,
          interaction_text: this.data.interaction_text,
          interaction_options: this.data.interaction_options
        };
      } else {
        question = this.question;
      }

      //creates explanation flag based if explanation exists and if grade has been changed
      let explanationFlag;
      if (this.view === "Single-Interaction" ) {
        explanationFlag = (
          this.data.explanation
          && typeof this.explanation_graded_single[this.data.id] != 'number'
        )
      } else if (this.view === "Student-Interaction") {
        explanationFlag = (
          this.data.explanation
          && typeof this.explanation_grade_student[this.data.interaction_attempt_id] != 'number'
        )
      } else  {
        explanationFlag = (
          this.data.explanation
          && typeof this.data.explanation_graded != 'number'
        )
      }
      let answerObject = {};
      switch(question.interaction_type) {
        case INTERACTION_TYPES.MULTIPLE_CHOICE:
          //multiple choice options(only supports multiple choice up to 52 choices)
          let grades = 'ABCDEFGHIJKLMNOPQRSTUVWXYabcdefghijklmnopqrstuvwxyz';
          let index;
          if(question.interaction_options.length > 0){
            question.interaction_options.forEach((item, i) => {
              if(item.interaction_option_id == this.data.interaction_option_ids) index = item.order ? item.order : i;
            })
          }
          //create answer display
          const correctAnswer = this.data.interaction_options.find((interaction) => interaction.correct === 1);
          
          if(explanationFlag){
            answerObject.display = `<button class="el-tag el-tag--warning student-answers mb-2" aria-label="Press space to open interaction viewer.">${grades[index]}</button>`;
          } else if (this.hideCorrectAnswerAlways) {
            answerObject.display = `<button class="el-tag el-tag--info student-answers mb-2" aria-label="Press space to open interaction viewer.">${grades[index]}</button>`;
          } else if((this.data.points_available === 0 && correctAnswer && correctAnswer.option === this.data.response_text) || (this.data.points_available > 0 && this.data.points_available === this.data.points_earned )) {
            answerObject.display = `<button class="el-tag el-tag--success student-answers mb-2" aria-label="Press space to open interaction viewer.">${grades[index]}</button>`;
          } else if (!this.data.response_text) {
            answerObject.display = '';
          } else {
            answerObject.display = `<button class="el-tag el-tag--danger student-answers mb-2" aria-label="Press space to open interaction viewer.">${grades[index]}</button>`  ;
          }
          //create list items for answers and feedback
          let answerArrayMC= [];
          if(question.interaction_options.length > 0){
            question.interaction_options.forEach((answer, i) => {
              if(i === index){
                (answer.correct === 1) ? answerArrayMC.push(`<li><span class="dot-selected"></span>${answer.option}</li><li class="correct-border"><span>Correct</span></li>`) : answerArrayMC.push(`<li><span class="dot-selected"></span>${answer.option}</li><li class="incorrect-border"><span>Incorrect</span></li>`);
              } else {
                answerArrayMC.push(`<li><span class="dot-incorrect"></span>${answer.option}</li>`);
              }
            })
          }
          //adds explanation if exists
          if(this.data.explanation){
            answerArrayMC.push(`<li class="feedback"><span class="bold">Explanation:</span>${this.data.explanation}</li>`);
          }
          //join list items and add to questionHTML
          let answersMC = answerArrayMC.join(' ');
          answerObject.questionHTML = `<div class="question-format"><div class="question-text">${question.interaction_text}</div></div><div class="question-format"><ul>${answersMC}</ul></div>`
          break;
        case INTERACTION_TYPES.CHECK_ALL_APPLY:
          //map through choices and push into choicesArray (only supports check all with 52 choices)
          let choicesArray = [];
          let choices = 'ABCDEFGHIJKLMNOPQRSTUVWXYabcdefghijklmnopqrstuvwxyz';
          //create number array of interaction_option_ids selected by user
          let selections = this.data.interaction_option_ids.split(',').map(item => {
            item = parseInt(item, 10)
            return item;
          });
          //create choices array based on selection
          if(question.interaction_options.length > 0){
            question.interaction_options.forEach((item, i) => {
              if(selections.includes(parseInt(item.interaction_option_id))) choicesArray.push(choices[i]);
            })
          }
          //joins choices with comma
          let choicesDisplay = choicesArray.slice().sort().join(', ');
          //creates answer array by looping through answers and comparing to correct index (need to adapt to new table changes)
          let answerArrayCA= [];
          let correctCA = true;
          if(question.interaction_options.length > 0){
            question.interaction_options.forEach((answer, i) => {
              //add answer choice based if student selected it or not
              if(selections.includes(answer.interaction_option_id)){
                answerArrayCA.push(`<li><span class="dot-selected"></span>${answer.option}</li>`);
              } else {
                answerArrayCA.push(`<li><span class="dot-incorrect"></span>${answer.option}</li>`);
              }
              //add correct, incorrect, or missed marker
              if(answer.correct === 1 && selections.includes(answer.interaction_option_id)){
                answerArrayCA.push(`<li class="correct-border"><span>Correct</span></li>`);
              } else if(answer.correct === 1 && !selections.includes(answer.interaction_option_id)){
                if (this.data.points_available === 0) correctCA = false;
                answerArrayCA.push(`<li class="missed-border"><span>Missed</span></li>`);
              } else if(answer.correct === 0 && selections.includes(answer.interaction_option_id)){
                if (this.data.points_available === 0) correctCA = false;
                answerArrayCA.push(`<li class="incorrect-border"><span>Incorrect</span></li>`);
              } else {
                answerArrayCA.push('')
              }
            })
          }
          //if explanation, creates answer display based on explanation flag, correct, or incorrect
          if(explanationFlag){
            answerObject.display =  `<button class="el-tag el-tag--warning student-answers mb-2">${choicesDisplay}</button>`
          } else if (this.hideCorrectAnswerAlways) {
            answerObject.display =  `<button class="el-tag el-tag--info student-answers mb-2">${choicesDisplay}</button>`;
          } else if(this.data.points_available === this.data.points_earned && correctCA) {
            answerObject.display =  `<button class="el-tag el-tag--success student-answers mb-2">${choicesDisplay}</button>`;
          } else {
            answerObject.display =   `<button class="el-tag el-tag--danger student-answers mb-2">${choicesDisplay}</button>`;
          }
          //add explanation if needed
          if(this.data.explanation){
            answerArrayCA.push(`<li class="feedback"><span class="bold">Explanation:</span>${this.data.explanation}</li>`);
          }
          //join list items and add to questionHTML
          let answersCA = answerArrayCA.join(' ');
          answerObject.questionHTML = `<div class="question-format"><div class="question-text">${question.interaction_text}</div></div><div class="question-format"><ul>${answersCA}</ul></div>`
          break;
        case INTERACTION_TYPES.FREE_RESPONSE:
          //if explanation, creates answer display based on explanation flag, ungraded, correct, incorrect, or partially correct
          if(explanationFlag && this.data.points_available > 0) {
            answerObject.display = `<button class="el-tag el-tag--warning student-answers mb-2"><i aria-hidden="true" class="v-icon material-icons theme--light small" style="font-size: 16px;">border_color</i></button>`;
          } else if(this.data.points_earned === null && this.data.points_available > 0){
            answerObject.display = `<button class="el-tag el-tag--warning student-answers mb-2"><i aria-hidden="true" class="v-icon material-icons theme--light small" style="font-size: 16px;">border_color</i></button>`;
          } else if (this.data.points_available == 0) {
            answerObject.display = `<button class="el-tag el-tag--info student-answers mb-2"><i aria-hidden="true" class="v-icon material-icons theme--light small" style="font-size: 16px;">border_color</i></button>`;
          } else if (this.data.points_earned == 0 || this.data.points_earned < this.data.points_available) {
            answerObject.display = `<button class="el-tag el-tag--danger student-answers mb-2"><i aria-hidden="true" class="v-icon material-icons theme--light small" style="font-size: 16px;">border_color</i></button>`;
          } else if (this.data.points_earned === this.data.points_available) {
            answerObject.display = `<button class="el-tag el-tag--success student-answers mb-2"><i aria-hidden="true" class="v-icon material-icons theme--light small" style="font-size: 16px;">border_color</i></button>`;
          }
          //if explanation, creates questionHTML with explanation. If not, creates questinHTML without
          answerObject.questionHTML = `<div class="question-format">
              <div class="question-text">${question.interaction_text}</div>
            </div>
            <div class="question-format">${this.data.response_text}</div>
            ${this.data.explanation ? `<div class="question-format feedback"><span class="bold">Explanation:</span>${this.data.explanation}</div>` : ''}
            ${this.data.interaction_attempt_comment ? `<div class="question-format feedback"><span class="bold">Feedback:</span>${this.data.interaction_attempt_comment}</div>` : ''}`;
          break;
        case INTERACTION_TYPES.SHORT_ANSWER:
          //creates answer for each fill in the blank answer
          let answerArraySA= [];
          let correctSA = true;
          if (this.data.points_available === 0) {
            if (question.interaction_options.length === 1) {
              const correctAnswers = question.interaction_options[0].option
                .toLowerCase()
                .split(this.delimiter)
                .map((answer) => answer.trim());
              correctSA = correctAnswers.includes(this.data.response_text.toLowerCase());
            } else {
              const responses = this.data.response_text.split(this.delimiter);
              const correctAnswers = question.interaction_options.map((option) => option.option.split(this.delimiter));
              correctSA = correctAnswers.every((answers, index) => {
                const lowercase = answers.map((answer) => answer.toLowerCase().trim());
                return lowercase.includes(responses[index].toLowerCase());
              });
            }
          }
          if(question.interaction_options.length > 0){
            question.interaction_options.forEach((answer, i) => {
              //create array of correct answers for this option
              let correctAnswers = answer.option.split(this.delimiter);
              let trimmed = correctAnswers.map(item => {
                return item.trim().toLowerCase();
              });
              let responseArray = this.data.response_text.split(this.delimiter);
              if(trimmed.includes(responseArray[i].toLowerCase())){
                //adds correct if answer is correct
                answerArraySA.push(`<li><i data-v-48ddbe66="" aria-hidden="true" class="v-icon material-icons theme--light" style="margin-right:10px;">subtitles</i>${answer.option}</li><li class="correct-border"><span>Correct</span></li>`);
              } else {
                //add incorrect if answer is incorrect
                answerArraySA.push(`<li><i data-v-48ddbe66="" aria-hidden="true" class="v-icon material-icons theme--light" style="margin-right:10px;">subtitles</i>${answer.option}</li><li class="incorrect-border"><span>Incorrect</span></li>`);
              }
            })
          }
          //if explanation, creates answer display based on explanation flag, correct, or incorrect
          if(explanationFlag){
            answerObject.display = `<button class="el-tag el-tag--warning student-answers mb-2">${this.data.response_text.replaceAll(this.delimiter, '｜')}</button>`;
          } else if (this.hideCorrectAnswerAlways) {
            answerObject.display = `<button class="el-tag el-tag--info student-answers mb-2">${this.data.response_text.replaceAll(this.delimiter, '｜')}</button>`
          } else if(this.data.points_available === this.data.points_earned && correctSA) {
            answerObject.display = `<button class="el-tag el-tag--success student-answers mb-2">${this.data.response_text.replaceAll(this.delimiter, '｜')}</button>`;
          } else {
            answerObject.display = `<button class="el-tag el-tag--danger student-answers mb-2">${this.data.response_text.replaceAll(this.delimiter, '｜')}</button>`;
          }
          //adds explanation if exists
          if(this.data.explanation){
            answerArraySA.push(`<li class="feedback"><span class="bold">Explanation:</span>${this.data.explanation}</li>`);
          }
          //join question and answer into HTML string
          let answersSA = answerArraySA.join(' ');
          let questionText = question.interaction_text.replace(/fill in blank/g,'______________________');
          answerObject.questionHTML = `<div class="question-format"><div class="question-text">${questionText}</div></div><div class="question-format"><ul>${answersSA}</ul></div>`;
          break;
        case INTERACTION_TYPES.PAUSE:
          //creates pause answer and question display
          answerObject.display = `<button class="el-tag el-tag--info student-answers mb-2">Pause</button>`
          answerObject.questionHTML = `<div class="question-format"><div class="question-text">${question.interaction_text}</div></div>`
          break;
        case INTERACTION_TYPES.IFRAME:
          //creates embedded answer and question display
          answerObject.display = `<button class="el-tag el-tag--info student-answers mb-2"><i aria-hidden="true" class="v-icon material-icons theme--light cursor" style="font-size: 16px;">public</i></button>`
          answerObject.questionHTML = `<div class="question-iframe"><iframe src="${decodeURIComponent(question.interaction_text)}"></iframe></div>`
          break;
        case INTERACTION_TYPES.FILE_VIEWER:
          answerObject.display = `<button class="el-tag el-tag--info student-answers mb-2"><i aria-hidden="true" class="v-icon material-icons theme--light cursor" style="font-size: 16px;">picture_as_pdf</i></button>`
          answerObject.questionHTML = `<div class="question-file-viewer"><iframe src="${decodeURIComponent(question.interaction_text)}"></iframe></div>`
        break;
        case INTERACTION_TYPES.POLL:
          //poll options
          const interactionOptionIds = this.data.interaction_option_ids.split(',').map(Number);
          let pollChoicesArray = [];
          let options = 'ABCDEFGHIJKLMNOPQRSTUVWXYabcdefghijklmnopqrstuvwxyz';
          let pollIndex;
          if(question.interaction_options.length > 0){
            question.interaction_options.forEach((opt, idx) => {
              const index = _.isFinite(opt.order) ? opt.order : idx;
              if(interactionOptionIds.includes(opt.interaction_option_id)) pollChoicesArray.push(options[index]);
            })
          }
          const pollChoicesDisplay = pollChoicesArray.slice().sort().join(', ');
          //if explanation,
          answerObject.display = (this.data.explanation) ? `<button class="el-tag el-tag--warning student-answers mb-2">${pollChoicesDisplay}</button>` : `<button class="el-tag el-tag--info student-answers mb-2">${pollChoicesDisplay}</button>`;
          //loops through possible answers to create answer items
          let answerArrayP= [];
          if(question.interaction_options.length > 0){
            question.interaction_options.forEach((answer, i) => {
              //if selected, shows which one the student selected
              (interactionOptionIds.includes(answer.interaction_option_id)) ? answerArrayP.push(`<li><span class="dot-selected"></span>${answer.option}</li>`) : answerArrayP.push(`<li><span class="dot-incorrect"></span>${answer.option}</li>`);
            })
          }
          //adds explanation if available
          if(this.data.explanation){
            answerArrayP.push(`<li class="feedback"><span class="bold">Explanation:</span>${this.data.explanation}</li>`);
          }
          //joins question and answers to create HTML string
          let answersP = answerArrayP.join(' ');
          answerObject.questionHTML = `<div class="question-format"><div class="question-text">${question.interaction_text}</div></div><div class="question-format"><ul>${answersP}</ul></div>`
          break;
        case INTERACTION_TYPES.DISCUSSION:
          //creates discussion answer and question display
          if (this.data.points_available === 0) {
            answerObject.display = `<button class="el-tag el-tag--info student-answers mb-2"><i aria-hidden="true" class="v-icon material-icons theme--light cursor" style="font-size: 16px;">chat</i></button>`;
          } else if (this.data.points_earned === null && this.data.points_available > 0) {
            answerObject.display = `<button class="el-tag el-tag--warning student-answers mb-2"><i aria-hidden="true" class="v-icon material-icons theme--light cursor" style="font-size: 16px;">chat</i></button>`;
          } else if (this.data.points_earned == 0 || this.data.points_earned < this.data.points_available) {
            answerObject.display = `<button class="el-tag el-tag--danger student-answers mb-2"><i aria-hidden="true" class="v-icon material-icons theme--light cursor" style="font-size: 16px;">chat</i></button>`;
          } else if (this.data.points_earned === this.data.points_available) {
            answerObject.display = `<button class="el-tag el-tag--success student-answers mb-2"><i aria-hidden="true" class="v-icon material-icons theme--light cursor" style="font-size: 16px;">chat</i></button>`;
          }
          answerObject.questionHTML = `<div class="question-format"><div class="question-text">${question.interaction_text}</div></div><div class="question-format"><div class="center"><button type="button" class="branded-background text-white button theme--light"><div class="button__content">Show Discussion</div></button></div></div>`;
          break;
        default:
          answerObject.display = '<span>-</span>';
          answerObject.questionHTML = '<div class="question-format"><span>No Student Data</span></div>';
          break;
      }
      if (_.get(this, 'data.points.length')) {
        this.badgeText = this.data.points;
      } else if (
        _.has(this.data, 'points_available')
        && _.has(this.data, 'points_earned')
      ) {
        this.badgeText = `${_.isInteger(this.data.points_earned) ? this.data.points_earned : '-'}/${this.data.points_available}`;
      }
      if (answerObject.display.includes('el-tag--success')) {
        this.selectedBadgeColor = this.badgeColors.success;
      } else if (answerObject.display.includes('el-tag--danger')) {
        this.selectedBadgeColor = this.badgeColors.danger;
      } else if (answerObject.display.includes('el-tag--warning')) {
        this.selectedBadgeColor = this.badgeColors.warning;
      } else if (answerObject.display.includes('el-tag--info')) {
        this.selectedBadgeColor = this.badgeColors.info;
      }
      this.display = answerObject.display;
    }
  },
  beforeMount() {
    this.loadData();
  },
  watch: {
    saveCount: function(n, o) {
      if (this.savedGrade === this.data.interaction_attempt_id) {
        this.loadData();
      }
    }
  }
};
</script>

<style lang="scss">
.full-width, .v-badge {
  width: 100%;
}

.v-badge.points-badge,
.v-badge.teacher-badge, 
.v-badge.student-badge {
  .v-badge__badge {
    height: 22px;
    width: auto;
    min-width: 22px;
    padding: 0px 2px 1px;
    letter-spacing: 0;
    text-indent: 0;
    border-radius: 10px;

    span {
      font-size: 12px;
    }
  }
}
</style>